<template>
  <v-container class="fill-height auth-page" fluid>
    <v-row align="center" justify="center">
      <v-col cols="12" sm="8" md="6" lg="4">
        <AuthBrandHeader />

        <v-card class="auth-card" flat>
          <v-card-text class="pa-6">
            <!-- Registration success message -->
            <div v-if="registrationComplete" class="text-center py-4">
              <v-icon color="success" size="56">mdi-email-check</v-icon>
              <h1 class="auth-heading mt-4" style="text-align: center">
                Check your email
              </h1>
              <p class="auth-subtext" style="text-align: center">
                We've sent a verification link to
                <strong>{{ registeredEmail }}</strong
                >.
              </p>
              <p class="text-body-2" style="color: var(--text-secondary)">
                Please click the link in your email to verify your account
                before logging in.
              </p>
              <p
                v-if="continuesAfterSignIn"
                class="text-body-2"
                style="color: var(--text-secondary)"
              >
                Once you've verified and logged in, you'll continue right where
                you left off.
              </p>
              <v-divider class="my-4"></v-divider>
              <p class="text-body-2" style="color: var(--text-secondary)">
                Didn't receive the email?
                <a
                  href="#"
                  @click.prevent="resendVerification"
                  class="auth-text-link"
                >
                  Resend verification email
                </a>
              </p>
            </div>

            <!-- Registration form -->
            <template v-else>
              <!-- Referral banner -->
              <div
                v-if="refPreview && refPreview.link_status === 'valid'"
                class="referral-banner referral-banner--valid mb-4"
              >
                <v-icon color="orange">mdi-gift-outline</v-icon>
                <div>
                  <div class="font-weight-bold">
                    {{ refPreview.referrer_name || "A friend" }} invited you
                  </div>
                  <div class="text-body-2">
                    You'll both get
                    <strong
                      >{{ refPreview.credits_on_offer }} free credits</strong
                    >
                    once you verify your email.
                  </div>
                </div>
              </div>
              <div
                v-else-if="refPreview && refPreview.link_status === 'exhausted'"
                class="referral-banner referral-banner--warn mb-4"
              >
                <v-icon color="grey">mdi-link-off</v-icon>
                <div class="text-body-2">
                  This invitation link is no longer valid. You can still
                  register - the referral bonus just isn't available.
                </div>
              </div>
              <div
                v-else-if="refPreview && refPreview.link_status === 'expired'"
                class="referral-banner referral-banner--warn mb-4"
              >
                <v-icon color="grey">mdi-clock-alert-outline</v-icon>
                <div class="text-body-2">
                  This invitation has expired. You can still register - the
                  referral bonus just isn't available.
                </div>
              </div>

              <h1 class="auth-heading">Create your account</h1>
              <p v-if="forcePaidPlans" class="auth-subtext">
                {{ $config.PAID_REGISTRATION_SUBTITLE || "Create your account, then choose a plan to get started." }}
              </p>
              <p v-else-if="$config.REGISTRATION_SUBTITLE" class="auth-subtext">
                {{ $config.REGISTRATION_SUBTITLE }}
              </p>
              <p v-else-if="starterCreditGrant.amount > 0" class="auth-subtext">
                {{ starterCreditGrant.amount }} starter credits once to run
                your first {{ $config.UNIT_NOUN }}. No card required.
              </p>
              <p v-else class="auth-subtext">Create your account to get started. No card required.</p>

              <!-- Renders only on apps with a Google client id configured.
                   Google emails are pre-verified, so this path skips the
                   verification screen entirely. -->
              <GoogleAuthButton mode="register" :ref-token="refToken" />

              <v-form
                ref="form"
                class="auth-form-compact"
                v-model="valid"
                autocomplete="off"
                @submit.prevent="register"
              >
                <v-text-field
                  v-model="name"
                  hide-details="auto"
                  :rules="nameRules"
                  label="Name"
                  prepend-icon="mdi-account"
                  autocomplete="off"
                  outlined
                  dense
                  required
                ></v-text-field>

                <v-text-field
                  v-model="email"
                  hide-details="auto"
                  :rules="emailRules"
                  label="Email"
                  prepend-icon="mdi-email"
                  type="email"
                  autocomplete="off"
                  outlined
                  dense
                  required
                ></v-text-field>

                <v-text-field
                  v-model="password"
                  hide-details="auto"
                  :rules="passwordRules"
                  label="Password"
                  prepend-icon="mdi-lock"
                  :append-icon="showPassword ? 'mdi-eye' : 'mdi-eye-off'"
                  :type="showPassword ? 'text' : 'password'"
                  @click:append="showPassword = !showPassword"
                  autocomplete="new-password"
                  outlined
                  dense
                  required
                ></v-text-field>

                <v-text-field
                  v-model="passwordConfirm"
                  hide-details="auto"
                  :rules="passwordConfirmRules"
                  label="Confirm password"
                  prepend-icon="mdi-lock-check"
                  :append-icon="showPasswordConfirm ? 'mdi-eye' : 'mdi-eye-off'"
                  :type="showPasswordConfirm ? 'text' : 'password'"
                  @click:append="showPasswordConfirm = !showPasswordConfirm"
                  autocomplete="new-password"
                  outlined
                  dense
                  required
                ></v-text-field>

                <v-btn
                  class="auth-submit-btn mt-2"
                  type="submit"
                  block
                  large
                  :disabled="!valid || loading"
                  :loading="loading"
                >
                  Create account
                </v-btn>
              </v-form>
            </template>
          </v-card-text>

          <template v-if="registrationComplete">
            <v-divider></v-divider>
            <v-card-actions class="justify-center pa-4">
              <v-btn class="auth-submit-btn" large :to="loginLink">
                Go to login
              </v-btn>
            </v-card-actions>
          </template>

          <template v-else>
            <v-divider></v-divider>
            <v-card-text class="auth-aux-line">
              Already have an account?
              <router-link :to="loginLink" class="auth-text-link">
                Log in
              </router-link>
            </v-card-text>
          </template>
        </v-card>

        <div v-if="!registrationComplete && paidTrustLine" class="auth-trust-line">
          <template v-for="(part, index) in paidTrustLine">
            <span v-if="index" :key="'sep' + index" class="auth-trust-sep">·</span>
            <span :key="'part' + index">{{ part }}</span>
          </template>
        </div>
        <div
          v-else-if="!registrationComplete && forcePaidPlans"
          class="auth-trust-line"
        >
          Choose a plan after signing up
          <span class="auth-trust-sep">·</span>
          Cancel anytime
        </div>
        <div v-else-if="!registrationComplete" class="auth-trust-line">
          No credit card required
          <span class="auth-trust-sep">·</span>
          <span v-if="$config.REGISTRATION_TRUST_HIGHLIGHT">{{ $config.REGISTRATION_TRUST_HIGHLIGHT }}</span>
          <span v-else-if="starterCreditGrant.amount > 0">{{ starterCreditGrant.amount }} starter credits once</span>
          <span v-else>Get started with your own account</span>
          <span class="auth-trust-sep">·</span>
          Cancel anytime
        </div>

        <div class="auth-footer-links">
          <a v-if="landingUrl" :href="landingUrl" target="_blank" rel="noopener"
            >About {{ $config.productName }}</a
          >
          <span v-if="landingUrl" class="auth-footer-sep">·</span>
          <router-link v-if="$config.SUPPORT_PATH" :to="$config.SUPPORT_PATH">Support</router-link>
          <a v-else :href="'mailto:' + $config.SUPPORT_EMAIL">Support</a>
        </div>

        <!-- Error Alert -->
        <v-alert
          v-if="error"
          type="error"
          dismissible
          class="mt-4"
          @input="clearError"
        >
          {{ error }}
        </v-alert>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { mapGetters } from "vuex";
import api from "../services/api";
import config from "@/config";
import AuthBrandHeader from "../components/AuthBrandHeader.vue";
import GoogleAuthButton from "../components/GoogleAuthButton.vue";
import {
  isMember,
  authQuery,
  rememberAuthNext,
  takeAuthNext
} from "@shared/utils/memberFunnel";

export default {
  name: "RegisterView",
  components: {
    AuthBrandHeader,
    GoogleAuthButton
  },
  data() {
    return {
      valid: false,
      name: "",
      email: "",
      password: "",
      passwordConfirm: "",
      showPassword: false,
      showPasswordConfirm: false,
      registrationComplete: false,
      registeredEmail: "",
      continuesAfterSignIn: false,
      // Referral link handling - populated from ?ref=<token> in mounted()
      refToken: "",
      refPreview: null,
      refPreviewLoading: false,
      nameRules: [
        v => !!v || "Name is required",
        v => v.length >= 2 || "Name must be at least 2 characters"
      ],
      emailRules: [
        v => !!v || "Email is required",
        v => /.+@.+\..+/.test(v) || "Email must be valid"
      ],
      passwordRules: [
        v => !!v || "Password is required",
        v => v.length >= 6 || "Password must be at least 6 characters"
      ]
    };
  },
  computed: {
    ...mapGetters(["loading", "error", "forcePaidPlans", "starterCreditGrant"]),
    // A paid-only app may name its own trust line ($config.PAID_TRUST_LINE).
    paidTrustLine() {
      var line = this.$config.PAID_TRUST_LINE;
      return this.forcePaidPlans && Array.isArray(line) && line.length ? line : null;
    },
    landingUrl() {
      if (typeof this.$config.LANDING_PAGE_URL === 'string') return this.$config.LANDING_PAGE_URL;
      return process.env.VUE_APP_LANDING_PAGE_URL || "https://multisaas.com";
    },
    passwordConfirmRules() {
      return [
        v => !!v || "Password confirmation is required",
        v => v === this.password || "Passwords do not match"
      ];
    },
    // Signing in instead keeps the destination (?next=, ?plan=).
    loginLink() {
      return { path: "/login", query: authQuery(this.$route.query) };
    }
  },
  async mounted() {
    // Members have nothing to register: send them straight on to where the
    // link was taking them (for example a landing page plan button).
    if (isMember(this.$store) && authQuery(this.$route.query).next) {
      this.$router.replace(takeAuthNext(this.$route.query)).catch(() => {});
      return;
    }
    // No referral programme on this app: ignore any ?ref= entirely, so a
    // shared link still lands on a clean, working register page (and no
    // ref_token is sent with the registration).
    if (config.REFERRALS_ENABLED === false) return;
    var token =
      this.$route && this.$route.query && this.$route.query.ref
        ? String(this.$route.query.ref).trim()
        : "";
    if (token) {
      this.refToken = token;
      await this.fetchReferralPreview();
    }
  },
  methods: {
    async fetchReferralPreview() {
      if (!this.refToken) return;
      this.refPreviewLoading = true;
      try {
        var resp = await api.previewReferralToken(this.refToken);
        this.refPreview = resp && resp.data ? resp.data : null;
      } catch (err) {
        // Soft failure - registration still works, just no banner
        console.warn("Could not preview referral token:");
        this.refPreview = null;
      } finally {
        this.refPreviewLoading = false;
      }
    },
    async register() {
      if (this.$refs.form.validate()) {
        // Declared outside try: the error handler below reads it.
        let payload = null;
        try {
          payload = {
            name: this.name,
            email: this.email,
            password: this.password,
            password_confirm: this.passwordConfirm,
            ref_token: this.refToken || null
          };

          // Anonymous-trial upgrade: a signed-in guest registering keeps
          // their account (and images) - the backend converts the guest row
          // in place when it receives the guest session token. Decide on the
          // TOKEN, not only the profile: a slow /users/me must never cause a
          // silent parallel account (the user's images would seem to vanish).
          const user = this.$store.getters.currentUser;
          const token = this.$store.state.token;
          if (token && (!user || user.is_anonymous)) {
            payload.anonymous_token = token;
          }

          const result = await this.$store.dispatch("register", payload);

          if (result && result.upgraded) {
            // Still authenticated as the same (now real) account: adopt the
            // fresh token after server-side revocation of the guest credentials,
            // refresh the profile, and continue to wherever the funnel was
            // headed (e.g. /subscribe carrying the picked plan).
            // Verification email is on its way in parallel.
            if (result.access_token) {
              this.$store.commit("SET_USER", null);
              this.$store.commit("SET_TOKEN", result.access_token);
            }
            await this.$store.dispatch("fetchCurrentUser");
            this.$router
              .push(takeAuthNext(this.$route.query) || { path: "/" })
              .catch(() => {});
            return;
          }

          // Show verification email sent message. The destination waits in
          // this browser until the new member verifies and signs in.
          this.continuesAfterSignIn = rememberAuthNext(this.$route.query);
          this.registeredEmail = this.email;
          this.registrationComplete = true;
        } catch (error) {
          // Error is handled by the store; make the one confusing case
          // explicit instead of leaving the generic message.
          if (payload && payload.anonymous_token && error && error.response &&
              error.response.status === 401) {
            this.$store.commit(
              "SET_ERROR",
              "Your guest session has expired, so your earlier images can't be attached to this account. Reload the page and register again."
            );
          }
          console.error("Registration failed");
        }
      }
    },
    async resendVerification() {
      try {
        await this.$store.dispatch(
          "resendVerificationEmail",
          this.registeredEmail
        );
        // Show success message
        this.$store.commit("SET_ERROR", null);
        alert("Verification email sent! Please check your inbox.");
      } catch (error) {
        console.error("Failed to resend verification:");
      }
    },
    clearError() {
      this.$store.commit("SET_ERROR", null);
    }
  }
};
</script>

<style scoped>
.referral-banner {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px 14px;
  border-radius: 8px;
  border-left: 4px solid #fb923c;
}

.referral-banner--valid {
  background: linear-gradient(
    90deg,
    rgba(251, 146, 60, 0.14) 0%,
    rgba(251, 146, 60, 0.06) 100%
  );
  border: 1px solid rgba(251, 146, 60, 0.4);
  border-left: 4px solid #fb923c;
}

.referral-banner--warn {
  background: rgba(0, 0, 0, 0.04);
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-left: 4px solid #9e9e9e;
}
</style>
